/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Turning a place name into coordinates.
 *
 * `event_activity_route.start_point` and friends are geo_point columns, but no
 * admin knows a start line's latitude. They know "Kanteerava Stadium". This
 * looks the name up against OpenStreetMap's Nominatim service and fills the
 * coordinates in, so typing numbers by hand becomes the fallback rather than
 * the only option.
 *
 * Deliberately a plain fetch against a public endpoint — no map library is
 * added to the admin for this, and the stored value is unchanged either way.
 */

const ENDPOINT = 'https://nominatim.openstreetmap.org/search';

/** Nominatim asks callers to identify themselves. */
export const GEOCODE_HEADERS = { Accept: 'application/json' };

export const MAX_SUGGESTIONS = 6;

export function geocodeUrl(query) {
  const params = new URLSearchParams({
    q: query, format: 'json', limit: String(MAX_SUGGESTIONS), addressdetails: '0',
  });
  return `${ENDPOINT}?${params.toString()}`;
}

const isNumeric = (value) => value !== '' && value !== null && value !== undefined
  && Number.isFinite(Number(value));

/** Provider rows -> [{label, lat, lng}], dropping anything unusable. */
export function parseGeocodeResults(payload) {
  if (!Array.isArray(payload)) return [];
  return payload
    .filter((row) => isNumeric(row?.lat) && isNumeric(row?.lon))
    .slice(0, MAX_SUGGESTIONS)
    .map((row) => ({
      label: row.display_name || `${row.lat}, ${row.lon}`,
      lat: String(row.lat),
      lng: String(row.lon),
    }));
}

/** Looks a place up. Returns [] on any failure — search is a convenience. */
export async function geocode(query, { signal } = {}) {
  if (!query || !query.trim()) return [];
  try {
    const resp = await fetch(geocodeUrl(query.trim()), { headers: GEOCODE_HEADERS, signal });
    if (!resp.ok) return [];
    return parseGeocodeResults(await resp.json());
  } catch {
    return [];
  }
}
